import { useNotification } from "@context/NotificationContext";
import { getCurrentMovementService } from "@services/movementServicesApi";
import type { ShippingAgencyTransferPayload } from "@services/shipping-agency-transfer/types";
import { postShippingAgencyTransfer } from "@services/shippingAgencyTransferApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import mapRequestShippingAgencyTransferFormToPayload from "../mappers/shippingAgencyTransferMapper";
import { useShippingAgencyTransferStore } from "../store/useRequestShippingAgencyTransfer.store";

type useCreateShippingAgencyProps = {
  vesselCallId: string;
  ownersMatters?: string;
  vesselId?: string;
  resetForm: () => void;
  vesselCallStatus?: string;
  departureStatus?: string;
};

const LANG_BASE = "vessel-call.shipping-agency-transfer.notifications";
const DEPARTURE_IN_PROGRESS_STATUSES = [
  "DEPARTURE_AWAITING_MARINE_APPROVAL",
  "DEPARTURE_REQUESTED",
  "DEPARTURE_AWAITING_TERMINAL_APPROVAL",
  "MARINE_DEPARTMENT_AWAITING_APPROVAL",
  "PORT_TERMINAL_AWAITING_APPROVAL",
  "DEPARTURE_APPROVED",
];

export const useCreateShippingAgencyTransfer = (props: useCreateShippingAgencyProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const { vesselCallId, resetForm, vesselId, vesselCallStatus, departureStatus } = props;

  const mutation = useMutation({
    mutationFn: (payload: ShippingAgencyTransferPayload) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "ShippingAgencyTransfer",
          action: "submit_shipping_agency_transfer",
          entityType: "shipping_agency_transfer",
        },
        (transactionId) =>
          postShippingAgencyTransfer({
            service: "bff-vessel-call",
            shippingAgencyTransferPayload: payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: () => {
      resetForm();
      notify({
        title: t(`${LANG_BASE}.request-submitted-success-title`),
        description: t(`${LANG_BASE}.request-submitted-success-description`),
        type: "success",
      });

      queryClient.invalidateQueries({
        queryKey: ["getShippingAgencyTransfers", vesselCallId],
      });
      queryClient.invalidateQueries({
        queryKey: ["getLatestShippingAgencyTransfer", vesselCallId],
      });
    },
    onError: (err: any) => {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description:
          err?.response?.data?.detail ??
          err?.response?.data?.message ??
          err?.message ??
          t(`${LANG_BASE}.request-submitted-error-description`),
        type: "error",
      });
    },
  });

  const onSubmit = async () => {
    const isDepartureInProgress =
      DEPARTURE_IN_PROGRESS_STATUSES.includes(vesselCallStatus ?? "") ||
      DEPARTURE_IN_PROGRESS_STATUSES.includes(departureStatus ?? "");

    const isDeparted = vesselCallStatus === "DEPARTED";
    const isDepartureClosed = vesselCallStatus === "CLOSED";

    if (isDepartureInProgress) {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description: t(`${LANG_BASE}.departure-in-progress-error`),
        type: "error",
      });
      return;
    }

    if (isDeparted) {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description: t(`${LANG_BASE}.departure-completed-six-hours-error`),
        type: "error",
      });
      return;
    }

    if (isDepartureClosed) {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description: t(`${LANG_BASE}.departure-closed-error`),
        type: "error",
      });
      return;
    }

    const activeMovement = await getCurrentMovementService({
      service: "bff-vessel-call",
      vesselCallId: vesselCallId,
      vesselId: vesselId ?? "",
    });

    const hasActiveMovement = activeMovement?.length > 0;

    if (hasActiveMovement) {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description: t(`${LANG_BASE}.active-movement-services-error`),
        type: "error",
      });
      return;
    }

    const state = useShippingAgencyTransferStore.getState();
    const payload = mapRequestShippingAgencyTransferFormToPayload(vesselCallId, state);
    mutation.mutate(payload);
  };

  return { onSubmit };
};
